// Tracker maths, per quota system.
//
// Alchemer quotas are tracked by Ranger's hourly snapshots:
//   [{snapshot_time, data: {quotas: [{id, name, description, responses, limit}], total_completes}}]
// Wrangler calculates its quotas' history on demand (quota-history); snapshotsFromHistory turns
// that into the same shape, so both systems go through the same functions. Targets, "overall"
// and gains are always computed within one system: an Alchemer quota and its Wrangler copy
// would otherwise double the target.

export const SYSTEM_LABELS = { alchemer: 'Alchemer', wrangler: 'Wrangler' };
// One colour per system, used by chips and chart lines alike.
export const SYSTEM_COLOURS = { alchemer: '#00897b', wrangler: '#7b1fa2' };

export const quotaKey = (quota) => `${quota.system}:${quota.id}`;

export const fillPct = (responses, limit) => (limit > 0 ? (responses / limit) * 100 : 0);

/** Wrangler's quota-history reply as snapshot-shaped points (one per history point). */
export const snapshotsFromHistory = (history) => {
  if (!history?.points) return [];
  return history.points.map((time, i) => ({
    snapshot_time: time,
    data: {
      total_completes: history.survey?.complete?.[i] ?? null,
      quotas: history.quotas.map((q) => ({
        id: q.id, name: q.name, set_name: q.set_name || '', is_overall: Boolean(q.is_overall), description: '',
        responses: q.complete[i], limit: q.limit[i],
      })),
    },
  }));
};

export const snapshotTotalCompletes = (snapshot) => {
  if (snapshot?.data?.total_completes != null) return Number(snapshot.data.total_completes);
  const qs = snapshot?.data?.quotas || [];
  if (!qs.length) return null;
  return qs.reduce((s, q) => s + Number(q.responses), 0);
};

const totalLimit = (quotas) => quotas.reduce((s, q) => s + Number(q.limit), 0);

/**
 * The survey's target. A Wrangler overall quota's limit is the target itself. Otherwise, a
 * heuristic: limits summed per group, smallest sum. A Wrangler quota with a set name is grouped
 * by its set; any other quota (all Alchemer ones) by its name prefix (before ":").
 */
export const targetN = (quotas) => {
  if (!quotas.length) return 0;
  const overall = quotas.find((q) => q.is_overall);
  if (overall) return Number(overall.limit);
  const prefixLimits = {};
  for (const q of quotas) {
    const prefix = q.set_name ? `set:${q.set_name}` : (q.name.includes(':') ? q.name.split(':')[0].trim() : '__all__');
    prefixLimits[prefix] = (prefixLimits[prefix] || 0) + Number(q.limit);
  }
  const sums = Object.values(prefixLimits);
  return sums.length ? Math.min(...sums) : totalLimit(quotas);
};

export const snapshotOverall = (snapshot) => {
  const completes = snapshotTotalCompletes(snapshot);
  const qs = snapshot?.data?.quotas || [];
  const totalL = totalLimit(qs);
  if (!completes || !totalL) return 0;
  if (snapshot?.data?.total_completes != null) {
    const target = targetN(qs);
    return target > 0 ? (completes / target) * 100 : 0;
  }
  return (completes / totalL) * 100;
};

export const snapshotQuotaPct = (snapshot, quotaId) => {
  const q = (snapshot?.data?.quotas || []).find((x) => String(x.id) === String(quotaId));
  if (!q) return null;
  return fillPct(Number(q.responses), Number(q.limit));
};

export const closestSnapshot = (snapshots, targetTs) => {
  if (!snapshots.length) return null;
  return snapshots.reduce((best, s) => {
    const d = Math.abs(new Date(s.snapshot_time).getTime() - targetTs);
    const bd = Math.abs(new Date(best.snapshot_time).getTime() - targetTs);
    return d < bd ? s : best;
  });
};

const DAY_MS = 24 * 3600 * 1000;
const HOUR_MS = 3600 * 1000;

/** One system's latest point, the point about 24h before it (at least 1h earlier), and overalls. */
export const systemSummary = (snapshots) => {
  const latest = snapshots.length ? snapshots[snapshots.length - 1] : null;
  if (!latest) return null;
  let dayAgo = null;
  if (snapshots.length >= 2) {
    const latestTs = new Date(latest.snapshot_time).getTime();
    const candidate = closestSnapshot(snapshots.slice(0, -1), latestTs - DAY_MS);
    if (candidate && latestTs - new Date(candidate.snapshot_time).getTime() >= HOUR_MS) dayAgo = candidate;
  }
  return {
    latest,
    dayAgo,
    completes: snapshotTotalCompletes(latest),
    target: targetN(latest.data?.quotas || []),
    overallNow: snapshotOverall(latest),
    overallThen: dayAgo ? snapshotOverall(dayAgo) : null,
  };
};

/**
 * Every tracked quota, from every system, with its comparisons against its own system's overall.
 * seriesBySystem: {alchemer: snapshots, wrangler: snapshots}.
 */
export const trackerRows = (seriesBySystem) => {
  const rows = [];
  for (const [system, snapshots] of Object.entries(seriesBySystem)) {
    const summary = systemSummary(snapshots || []);
    if (!summary) continue;
    for (const raw of summary.latest.data?.quotas || []) {
      const quota = { ...raw, system, responses: Number(raw.responses), limit: Number(raw.limit) };
      const pctNow = fillPct(quota.responses, quota.limit);
      let vsLastDay = null;
      let quotaGain = null;
      let overallGain = null;
      if (summary.dayAgo && summary.overallThen !== null) {
        const pctThen = snapshotQuotaPct(summary.dayAgo, quota.id);
        if (pctThen !== null) {
          quotaGain = pctNow - pctThen;
          overallGain = summary.overallNow - summary.overallThen;
          vsLastDay = quotaGain - overallGain;
        }
      }
      rows.push({
        ...quota, key: quotaKey(quota), vsAllTime: pctNow - summary.overallNow, vsLastDay,
        // The figures behind the comparisons, for explaining them.
        pctNow, overallNow: summary.overallNow, quotaGain, overallGain,
      });
    }
  }
  return rows;
};
